import React, { useCallback, useMemo, useState } from "react";
import {
    Button as RebaseButton,
    Chip as RebaseChip,
    Separator as RebaseSeparator,
    TextField as RebaseTextField,
    Typography as RebaseTypography,
    cls,
    defaultBorderMixin
} from "@rebasepro/ui";

import {
    ACTIVITY_COLOR,
    ACTIVITY_LABEL,
    contactLabel,
    sinceLabel,
    type ActivityKind,
    type ActivityRow,
    type ContactRow
} from "./_prospects_shared";
import { threadTitle, type ThreadRow } from "./_email_shared";
import { ConversationLink } from "./ConversationPanel";

const Button = RebaseButton as React.ComponentType<any>;
const Chip = RebaseChip as React.ComponentType<any>;
const Separator = RebaseSeparator as React.ComponentType<any>;
const TextField = RebaseTextField as React.ComponentType<any>;
const Typography = RebaseTypography as React.ComponentType<any>;

/**
 * What was done with this employer, newest first.
 *
 * The prospect's `notes` field held all of this and could answer none of it:
 * "contacted twice this week by two people" and "not touched since March" read
 * identically in a text box. Every act is a row here — the status changes, the
 * emails, the calls, the discovery runs — so the account has a history.
 *
 * Nothing on this panel edits or deletes an entry, deliberately. A log that can
 * be rewritten is the text box again with extra steps; standing context about
 * the account belongs in "Contexto", which is what that field is for now.
 */

/** The kinds a person logs by hand. `status` and `discovery` are written by the app. */
const MANUAL_KINDS: { id: ActivityKind; label: string }[] = [
    { id: "note", label: "Nota" },
    { id: "call", label: "Llamada" },
    { id: "email", label: "Email" },
    { id: "meeting", label: "Reunión" }
];

/** A short absolute stamp, because a log is read as a sequence of days. */
function stamp(iso: string | null | undefined): string {
    if (!iso) return "";
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return "";
    return date.toLocaleDateString("es-ES", { day: "numeric", month: "short", year: "numeric" });
}

/**
 * One line on the timeline: either a logged act or a reply that arrived.
 *
 * The log could say we wrote and never that they answered — the reply landed in
 * "Correo" against the same employer and stopped there, so the account's history
 * was a half-truth at precisely the moment it mattered. These are merged rather
 * than the activity table being written to on ingest: a reply is already stored,
 * once, in the messages table, and a second copy of it in `prospect_activity`
 * would be a copy that goes stale the moment somebody answers from Gmail.
 */
interface TimelineItem {
    id: string;
    at: string | null;
    kind: ActivityKind;
    body: string;
    actor?: string | null;
    contactId?: string | null;
    /** Set on the entries that came from a conversation rather than the log. */
    threadId?: string;
}

export function ProspectActivityPanel({
    entries,
    threads,
    contacts,
    busy,
    onAdd,
    onConversationChanged
}: {
    entries: ActivityRow[];
    /**
     * Conversations with this employer. Only their *inbound* half is put on the
     * timeline: the outbound half is already logged by `functions/send-email`,
     * and showing both would print every sent email twice.
     */
    threads?: ThreadRow[];
    contacts: ContactRow[];
    busy: boolean;
    onAdd: (kind: ActivityKind, body: string) => Promise<void> | void;
    /** After a conversation opened from here was read or answered. */
    onConversationChanged?: () => void;
}) {
    const [kind, setKind] = useState<ActivityKind>("note");
    const [body, setBody] = useState("");

    const contactName = useMemo(() => {
        const map = new Map<string, string>();
        for (const contact of contacts) map.set(contact.id, contactLabel(contact));
        return map;
    }, [contacts]);

    const ordered = useMemo<TimelineItem[]>(() => {
        const items: TimelineItem[] = entries.map(entry => ({
            id: entry.id,
            at: entry.created_at ?? null,
            kind: (entry.kind ?? "note") as ActivityKind,
            body: entry.body || "—",
            actor: entry.actor,
            contactId: entry.contact_id
        }));

        for (const thread of threads ?? []) {
            // `last_inbound_at`, not `last_message_at`: the timeline is asking
            // when *they* wrote, and stamping it with our own last reply would
            // put their answer after it.
            if (!thread.last_inbound_at) continue;
            items.push({
                id: `thread:${thread.id}`,
                at: thread.last_inbound_at,
                kind: "email",
                body: `Respondieron: "${threadTitle(thread)}"`,
                threadId: thread.id
            });
        }

        return items.sort((a, b) => (b.at ?? "").localeCompare(a.at ?? ""));
    }, [entries, threads]);

    const submit = useCallback(async () => {
        const text = body.trim();
        if (!text) return;
        await onAdd(kind, text);
        setBody("");
    }, [body, kind, onAdd]);

    return (
        <div className="flex flex-col gap-3">
            <Typography variant="subtitle2" component="h3">Qué hemos hecho con esta empresa</Typography>

            <div className="flex flex-col gap-2">
                <div className="flex gap-1.5 flex-wrap">
                    {MANUAL_KINDS.map(option => (
                        <Button
                            key={option.id}
                            size="small"
                            variant={kind === option.id ? "filled" : "outlined"}
                            onClick={() => setKind(option.id)}
                        >
                            {option.label}
                        </Button>
                    ))}
                </div>
                <TextField
                    size="small"
                    label="Qué pasó"
                    multiline
                    rows={2}
                    value={body}
                    onChange={(e: any) => setBody(e.target.value)}
                />
                <div>
                    <Button size="small" disabled={busy || !body.trim()} onClick={submit}>
                        Registrar
                    </Button>
                </div>
            </div>

            <Separator />

            {ordered.length === 0 && (
                <Typography variant="body2" color="secondary">
                    Nada registrado todavía. Cada cambio de estado, email y búsqueda de personas
                    aparecerá aquí automáticamente.
                </Typography>
            )}

            <div className="flex flex-col">
                {ordered.map((entry, index) => {
                    const who = entry.contactId ? contactName.get(entry.contactId) : null;
                    return (
                        <div
                            key={entry.id}
                            className={cls(
                                "flex gap-3 py-2",
                                index < ordered.length - 1 && "border-b",
                                index < ordered.length - 1 && defaultBorderMixin
                            )}
                        >
                            <div className="pt-0.5">
                                <Chip size="small" colorScheme={entry.threadId ? "cyanLighter" : ACTIVITY_COLOR[entry.kind]}>
                                    {entry.threadId ? "Respuesta" : ACTIVITY_LABEL[entry.kind]}
                                </Chip>
                            </div>
                            <div className="grow min-w-0">
                                <Typography variant="body1" className="whitespace-pre-wrap break-words">
                                    {entry.body}
                                </Typography>
                                <Typography variant="body2" color="secondary">
                                    {stamp(entry.at)} · {sinceLabel(entry.at)}
                                    {entry.actor ? ` · ${entry.actor}` : ""}
                                    {who ? ` · ${who}` : ""}
                                    {entry.threadId && (
                                        <>
                                            {" · "}
                                            {/* The whole conversation, not a
                                                second copy of it here — in the
                                                side panel, over this employer,
                                                with the reply box under it. */}
                                            <ConversationLink id={entry.threadId} onChanged={onConversationChanged}>
                                                abrir la conversación
                                            </ConversationLink>
                                        </>
                                    )}
                                </Typography>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
